import type { CapabilityCreateInputSchema } from "../../generated/core-api";
import {
  MAX_ALTERNATE_QUERIES,
  MAX_QUESTION_LENGTH,
} from "../types";

export const DEFAULT_VECTOR_KB_QUESTION_DESCRIPTION =
  "Natural-language question to retrieve evidence for. Also used as the rerank query.";

export const DEFAULT_VECTOR_KB_ALTERNATE_QUERIES_DESCRIPTION =
  "Optional keyword centroids (statute cites, leave types, corpus terms). Maximum 4. Omit if unsure.";

export const DEFAULT_VECTOR_KB_TOP_K_DESCRIPTION =
  "Maximum number of chunks to return after rerank (optional). Clamped to the capability's configured maximum when supplied.";

const ALLOWED_INPUT_PROPERTIES = new Set([
  "question",
  "alternate_queries",
  "top_k",
]);
const OPTIONAL_INPUT_PROPERTIES = new Set(["alternate_queries", "top_k"]);

export type VectorKbToolParameters = {
  questionDescription: string;
  includeAlternateQueries: boolean;
  alternateQueriesDescription: string;
  includeTopK: boolean;
  topKDescription: string;
};

export function defaultVectorKbToolParameters(): VectorKbToolParameters {
  return {
    questionDescription: DEFAULT_VECTOR_KB_QUESTION_DESCRIPTION,
    includeAlternateQueries: true,
    alternateQueriesDescription: DEFAULT_VECTOR_KB_ALTERNATE_QUERIES_DESCRIPTION,
    includeTopK: true,
    topKDescription: DEFAULT_VECTOR_KB_TOP_K_DESCRIPTION,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function propertyDescription(
  properties: Record<string, unknown>,
  name: string,
  fallback: string,
): string {
  const property = properties[name];
  if (!isRecord(property) || typeof property.description !== "string") {
    return fallback;
  }
  const description = property.description.trim();
  return description || fallback;
}

function isStringArrayItems(items: unknown): boolean {
  if (items === undefined) {
    return true;
  }
  return isRecord(items) && items.type === "string";
}

/**
 * Structural check aligned with Pacific.Agentic.Api's
 * ``_validate_retrieval_vector_kb_input_schema``.
 *
 * Allowed: custom property descriptions, and omitting optional
 * ``alternate_queries`` / ``top_k``.
 * Rejected: wrong names/types, extra properties, required optionals,
 * missing or non-false ``additionalProperties``, non-string ``required`` entries.
 */
export function isValidVectorKbInputSchema(schema: unknown): boolean {
  if (!isRecord(schema) || schema.type !== "object") {
    return false;
  }
  if (schema.additionalProperties !== false) {
    return false;
  }
  if (!isRecord(schema.properties)) {
    return false;
  }

  const { properties } = schema;
  const propertyNames = Object.keys(properties);
  if (propertyNames.some((name) => !ALLOWED_INPUT_PROPERTIES.has(name))) {
    return false;
  }

  const question = properties.question;
  if (!isRecord(question) || question.type !== "string") {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(properties, "top_k")) {
    const topK = properties.top_k;
    if (!isRecord(topK) || topK.type !== "integer") {
      return false;
    }
  }

  if (Object.prototype.hasOwnProperty.call(properties, "alternate_queries")) {
    const alternates = properties.alternate_queries;
    if (!isRecord(alternates) || alternates.type !== "array") {
      return false;
    }
    if (!isStringArrayItems(alternates.items)) {
      return false;
    }
  }

  const requiredRaw = schema.required ?? [];
  if (!Array.isArray(requiredRaw)) {
    return false;
  }
  if (!requiredRaw.every((name): name is string => typeof name === "string")) {
    return false;
  }
  if (
    !requiredRaw.includes("question") ||
    requiredRaw.some((name) => OPTIONAL_INPUT_PROPERTIES.has(name))
  ) {
    return false;
  }
  return requiredRaw.every((name) =>
    Object.prototype.hasOwnProperty.call(properties, name),
  );
}

export function parseVectorKbToolParameters(
  schema: unknown,
): VectorKbToolParameters {
  const defaults = defaultVectorKbToolParameters();
  if (!isRecord(schema) || !isRecord(schema.properties)) {
    return defaults;
  }

  const { properties } = schema;
  return {
    questionDescription: propertyDescription(
      properties,
      "question",
      defaults.questionDescription,
    ),
    includeAlternateQueries: Object.prototype.hasOwnProperty.call(
      properties,
      "alternate_queries",
    ),
    alternateQueriesDescription: propertyDescription(
      properties,
      "alternate_queries",
      defaults.alternateQueriesDescription,
    ),
    includeTopK: Object.prototype.hasOwnProperty.call(properties, "top_k"),
    topKDescription: propertyDescription(
      properties,
      "top_k",
      defaults.topKDescription,
    ),
  };
}

export function buildVectorKbInputSchema(
  parameters: VectorKbToolParameters,
): CapabilityCreateInputSchema {
  const questionDescription =
    parameters.questionDescription.trim() ||
    DEFAULT_VECTOR_KB_QUESTION_DESCRIPTION;
  const properties: Record<string, unknown> = {
    question: {
      type: "string",
      description: questionDescription,
      maxLength: MAX_QUESTION_LENGTH,
    },
  };

  if (parameters.includeAlternateQueries) {
    properties.alternate_queries = {
      type: "array",
      description:
        parameters.alternateQueriesDescription.trim() ||
        DEFAULT_VECTOR_KB_ALTERNATE_QUERIES_DESCRIPTION,
      items: { type: "string" },
      maxItems: MAX_ALTERNATE_QUERIES,
    };
  }

  if (parameters.includeTopK) {
    properties.top_k = {
      type: "integer",
      description:
        parameters.topKDescription.trim() || DEFAULT_VECTOR_KB_TOP_K_DESCRIPTION,
      minimum: 1,
    };
  }

  return {
    type: "object",
    properties,
    required: ["question"],
    additionalProperties: false,
  };
}

export const VECTOR_KB_INPUT_SCHEMA = buildVectorKbInputSchema(
  defaultVectorKbToolParameters(),
);

export function listVectorKbToolParameterNames(
  parameters: VectorKbToolParameters,
): string[] {
  const names = ["question"];
  if (parameters.includeAlternateQueries) {
    names.push("alternate_queries");
  }
  if (parameters.includeTopK) {
    names.push("top_k");
  }
  return names;
}
